import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchSolution } from '../../api/solution';
import type { Difficulty } from '../../api/puzzles';
import { Board } from '../board/Board';

interface SolutionModalProps {
  date: string;
  difficulty: Difficulty;
  ownScore: number;
  onClose: () => void;
}

/** One of the three headline numbers (You / Target / Best). */
function Stat({ label, value, tone }: { label: string; value: number; tone: 'you' | 'plain' }) {
  return (
    <div className="flex flex-col items-center">
      <span className="text-[0.7rem] font-medium uppercase tracking-wide text-stone-400">
        {label}
      </span>
      <span
        className={`text-2xl font-bold tabular-nums ${
          tone === 'you' ? 'text-emerald-700' : 'text-stone-800'
        }`}
      >
        {value}
      </span>
    </div>
  );
}

/** Optimal play + alternate lines, unlocked once all lives are used. */
export function SolutionModal({ date, difficulty, ownScore, onClose }: SolutionModalProps) {
  const [lineIndex, setLineIndex] = useState(0);
  const query = useQuery({
    queryKey: ['solution', date, difficulty],
    queryFn: () => fetchSolution(date, difficulty),
  });

  const lines = query.data?.lines ?? [];
  const line = lines[lineIndex] ?? null;
  const hitTarget = query.data ? ownScore >= query.data.target_score : false;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="What was possible"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-sm rounded-2xl bg-white p-6 text-center shadow-xl"
        onClick={(ev) => ev.stopPropagation()}
      >
        <h2 className="text-lg font-bold text-stone-800">What was possible</h2>

        {query.isLoading ? (
          <p role="status" className="mt-6 text-stone-500">
            Loading solution…
          </p>
        ) : query.isError || !query.data ? (
          <p role="alert" className="mt-6 text-red-600">
            Couldn't load the solution.
          </p>
        ) : (
          <>
            <div className="mt-4 flex items-stretch justify-center gap-3">
              <Stat label="You" value={ownScore} tone={hitTarget ? 'you' : 'plain'} />
              <div className="w-px self-stretch bg-stone-200" aria-hidden="true" />
              <Stat label="Target" value={query.data.target_score} tone="plain" />
              <div className="w-px self-stretch bg-stone-200" aria-hidden="true" />
              <Stat label="Best" value={query.data.optimal_score} tone="you" />
            </div>

            {lines.length > 1 ? (
              <div
                role="group"
                aria-label="Solution lines"
                className="mt-5 flex items-center justify-center gap-2"
              >
                <button
                  type="button"
                  aria-label="Previous play"
                  disabled={lineIndex === 0}
                  onClick={() => setLineIndex((i) => Math.max(0, i - 1))}
                  className="flex h-8 w-8 items-center justify-center rounded-full border border-stone-300 text-stone-600 transition hover:bg-stone-50 disabled:cursor-not-allowed disabled:opacity-30"
                >
                  <span aria-hidden="true">‹</span>
                </button>
                <div className="min-w-36">
                  <p className="text-sm font-bold text-stone-800">
                    {lineIndex === 0 ? 'Best play' : `Line ${lineIndex + 1}`} · {line?.total_score}
                  </p>
                  <p className="text-xs text-stone-400">
                    {lineIndex + 1} of {lines.length}
                  </p>
                </div>
                <button
                  type="button"
                  aria-label="Next play"
                  disabled={lineIndex === lines.length - 1}
                  onClick={() => setLineIndex((i) => Math.min(lines.length - 1, i + 1))}
                  className="flex h-8 w-8 items-center justify-center rounded-full border border-stone-300 text-stone-600 transition hover:bg-stone-50 disabled:cursor-not-allowed disabled:opacity-30"
                >
                  <span aria-hidden="true">›</span>
                </button>
              </div>
            ) : null}

            {line ? (
              <>
                <div className="mx-auto mt-5 max-w-64 rounded-lg border border-stone-200 bg-stone-50 p-2">
                  <Board board={line.board} staged={[]} hidePremiumLabels testIdPrefix="sol-" />
                </div>
                <div
                  data-testid="solution-words"
                  className="mt-4 flex flex-wrap justify-center gap-1.5"
                >
                  {line.words.map((w, i) => (
                    <span
                      key={`${w.word}-${i}`}
                      className="inline-flex items-center gap-1 rounded-full bg-stone-100 px-2.5 py-1 text-sm"
                    >
                      <span className="font-semibold text-stone-800">{w.word}</span>{' '}
                      <span className="tabular-nums text-stone-500">{w.score}</span>
                    </span>
                  ))}
                </div>
              </>
            ) : null}
          </>
        )}

        <button
          type="button"
          onClick={onClose}
          className="mt-6 w-full rounded-lg bg-emerald-700 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-emerald-800"
        >
          Close
        </button>
      </div>
    </div>
  );
}
